import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, X, Zap, TrendingUp, AlertTriangle, Layers, Info, MousePointer2, RefreshCcw, CheckCircle, Loader2 } from 'lucide-react';
import { useAppContext } from '../../../core/App';

// --- IGNORE LIST (Safe Words) ---
const SAFE_WORDS = [
  'with', 'and', 'the', 'for', 'to', 'in', 'on', 'at', 'of', 'a', 'an', 
  'business', 'customer', 'result', 'results', 'project', 'projects', 
  'team', 'company', 'market', 'strategic', 'strategies', 'management',
  'organization', 'partners', 'development', 'internal', 'external',
  'expectations', 'mission', 'goal', 'goals', 'initiatives', 'role',
  'experience', 'work', 'skills', 'leader', 'leadership'
];

/**
 * THE MATCH MATRIX ENGINE
 * Compares user highlights against AI pedagogical tokens.
 */
const calculateAuditPerformance = (text, userSelectedIds, aiVerbs, aiRisks) => {
    let matchCount = 0;
    let falsePositives = 0;
    const paragraphs = text.split('\n');

    paragraphs.forEach((para, pIdx) => {
        const words = para.split(' ');
        words.forEach((word, wIdx) => {
            const wordId = `${pIdx}-${wIdx}`;
            if (userSelectedIds.includes(wordId)) {
                const clean = word.replace(/[^a-zA-Z0-9$%]/g, '').toLowerCase();
                
                // 1. SIGNAL DETECTION (The "Magnets")
                const isMetric = /[\d]/.test(clean) && (/[%$]/.test(clean) || clean.length > 4);
                const isStrongVerb = aiVerbs.some(v => clean === v.toLowerCase() || clean.startsWith(v.toLowerCase()));

                if (isMetric || isStrongVerb) {
                    matchCount++;
                } else {
                    falsePositives++;
                }
            }
        });
    });

    return { matchCount, falsePositives };
};

export default function RecruiterVision({ 
    viewMode = 'standard', // 'standard', 'audit', 'comparison'
    resumeText, 
    reportData, 
    onClose,
    userHighlights = [],
    setUserHighlights,
    timeLeft = 0,
    onProceed,
    onRecalibrate,
    hasEarnedAuditCxp,
    setHasEarnedAuditCxp,
    isReviewMode
}) {
  const { awardCxp, setToastMessage } = useAppContext();
  const [isBlurred, setIsBlurred] = useState(viewMode === 'standard');
  const [activeTip, setActiveTip] = useState(null); 

  // --- THE MASK STATE ---
  const scanProgress = Math.max(0, Math.min(1, (15 - timeLeft) / 15)); 
  const isAggressivelyBlurred = viewMode === 'audit' && timeLeft <= 7.5;
  const currentBlurState = isBlurred || isAggressivelyBlurred;
  
  // The "F-Pattern" Focus logic
  const maskStyle = {
    background: `radial-gradient(
      circle at 20% 20%, 
      transparent 0%, 
      transparent ${40 - (scanProgress * 30)}%, 
      rgba(15, 23, 42, ${0.4 + (scanProgress * 0.5)}) ${70 - (scanProgress * 20)}%, 
      rgba(15, 23, 42, 0.95) 100%
    )`,
    backdropFilter: `blur(${scanProgress * 8}px)`
  };

  // --- 1. EXTRACT DATA POINTS ---
  const dynamicHighlights = useMemo(() => reportData?.heatmap_rationales || [], [reportData]);

  const strongVerbs = useMemo(() => {
    const legacy = (reportData?.action_verb_analysis?.strong_verbs_used || []).map(v => v.toLowerCase());
    const dynamic = dynamicHighlights.filter(h => h.category === 'Action' || h.category === 'Verb').map(h => h.word.toLowerCase());
    return [...new Set([...legacy, ...dynamic])];
  }, [reportData, dynamicHighlights]);

  const riskTokens = useMemo(() => {
    const phrases = reportData?.ai_residue_check?.flagged_phrases || [];
    const dynamic = dynamicHighlights.filter(h => h.category === 'Risk').map(h => h.word.toLowerCase());
    const tokens = new Set([...dynamic]);
    phrases.forEach(phrase => {
      phrase.split(/\s+/).forEach(word => {
        const clean = word.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        if (clean.length > 3 && !SAFE_WORDS.includes(clean)) tokens.add(clean);
      });
    });
    return tokens;
  }, [reportData, dynamicHighlights]);

  // --- 2. MATCHING ALGORITHM & GAMIFICATION ---
  useEffect(() => {
    if (viewMode === 'comparison' && reportData && !hasEarnedAuditCxp && !isReviewMode) {
        
        // Execute the Match Matrix Engine
        const { matchCount } = calculateAuditPerformance(resumeText, userHighlights, strongVerbs, riskTokens);

        if (matchCount > 0 && setHasEarnedAuditCxp) {
            awardCxp('INTEGRITY_BONUS'); 
            setToastMessage(`Match Matrix: ${matchCount} Visual Magnets Found! (+25 CXP)`);
            setHasEarnedAuditCxp(true);
        }
    }
  }, [viewMode, reportData, hasEarnedAuditCxp, isReviewMode, userHighlights, resumeText, strongVerbs, riskTokens, awardCxp, setToastMessage, setHasEarnedAuditCxp]);

  // --- 3. COACHING LOGIC ---
  // --- REFACTORED: Flexible Dynamic Tooltip Lookup ---
  const handleTokenClick = (type, word) => {
    if (viewMode === 'audit') return; 

    const cleanTarget = word.toLowerCase().trim();
    const match = dynamicHighlights.find(h => {
        const cleanH = h.word.toLowerCase().replace(/[^a-zA-Z0-9$%]/g, '').trim();
        return cleanH === cleanTarget || cleanH.includes(cleanTarget) || cleanTarget.includes(cleanH);
    });

    if (match) {
        let color = "text-green-400";
        if (match.category === 'Action' || match.category === 'Verb') color = "text-cyan-400";
        if (match.category === 'Risk') color = "text-red-500";

        setActiveTip({ 
            title: `${match.category} Signal Detected`, 
            msg: match.contextualRationale, 
            color: color 
        });
    } else {
        if (type === 'impact') {
            setActiveTip({ title: "Impact Detected (Metric)", msg: `"${word}" acts as a 'visual anchor' for recruiters. Hard numbers prove measurable value.`, color: "text-green-400" });
        } else if (type === 'verb') {
            setActiveTip({ title: "Leadership Signal (Action)", msg: `"${word}" is a high-power verb. It signals ownership and initiative.`, color: "text-cyan-400" });
        } else if (type === 'risk') {
            setActiveTip({ title: "Vague Claim (Risk)", msg: `"${word}" is flagged as a cliché. Without a number attached, it reads as fluff.`, color: "text-red-500" });
        }
    }
  };

  const toggleHighlight = (wordId) => {
      if (viewMode !== 'audit' || !setUserHighlights) return;
      
      setUserHighlights(prev => {
          if (prev.includes(wordId)) return prev.filter(id => id !== wordId);
          return [...prev, wordId];
      });
  };

  // --- 4. TEXT PROCESSING & HIGHLIGHTING ---
  const processText = (text) => {
    if (!text) return null;
    const paragraphs = text.split('\n');

    return paragraphs.map((para, pIdx) => {
      if (!para.trim()) return <div key={pIdx} className="h-4" />;
      const words = para.split(' ');
      
      return (
        <div key={pIdx} className="mb-2 leading-relaxed text-sm md:text-base font-mono relative z-10">
          {words.map((word, wIdx) => {
            const wordId = `${pIdx}-${wIdx}`;
            const cleanWord = word.replace(/[^a-zA-Z0-9$%]/g, '').toLowerCase();
            const rawWord = word + " "; 
            
            const isYear = /^(19|20)\d{2}/.test(cleanWord);
            const isImpact = (!isYear && /[\d]/.test(cleanWord) && (/[%$]/.test(cleanWord) || cleanWord.length <= 3 || cleanWord.length > 4));
            const isVerb = strongVerbs.some(v => cleanWord === v || cleanWord.startsWith(v));
            const isRisk = riskTokens.has(cleanWord);
            
            const isUserHighlighted = userHighlights.includes(wordId);

            // --- VIEW MODE 1: INTERACTIVE AUDIT ---
            if (viewMode === 'audit') {
                return (
                    <motion.span
                        key={wordId}
                        whileTap={{ scale: 1.15 }}
                        onClick={(e) => { e.stopPropagation(); toggleHighlight(wordId); }}
                        className={`cursor-pointer rounded px-0.5 transition-colors duration-150 inline-block ${
                            isUserHighlighted 
                                ? 'bg-yellow-400 text-yellow-900 font-bold shadow-[0_0_8px_rgba(250,204,21,0.6)]' 
                                : currentBlurState 
                                    ? 'blur-[4px] opacity-30 text-slate-500 pointer-events-none select-none' 
                                    : 'text-slate-300 hover:bg-slate-800'
                        }`}
                    >
                        {rawWord}
                    </motion.span>
                );
            }

            // --- VIEW MODE 2: COMPARISON REVEAL ---
            if (viewMode === 'comparison') {
                if (isUserHighlighted && (isImpact || isVerb)) {
                    // Match!
                    return <span key={wordId} className="ring-2 ring-green-400 bg-green-900/60 text-green-300 font-bold drop-shadow-[0_0_8px_rgba(74,222,128,0.8)] rounded px-0.5">{rawWord}</span>;
                }
                if (isUserHighlighted && !isImpact && !isVerb) {
                    // False Positive
                    return <span key={wordId} className="bg-yellow-900/40 text-yellow-600 line-through decoration-red-500 rounded px-0.5">{rawWord}</span>;
                }
                if (!isUserHighlighted && isImpact) {
                    // Missed Impact
                    return <span key={wordId} onClick={(e) => { e.stopPropagation(); handleTokenClick('impact', cleanWord); }} className="border border-dashed border-green-500/50 text-green-500/70 font-bold cursor-help rounded px-0.5">{rawWord}</span>;
                }
                if (!isUserHighlighted && isVerb) {
                    // Missed Verb
                    return <span key={wordId} onClick={(e) => { e.stopPropagation(); handleTokenClick('verb', cleanWord); }} className="border border-dashed border-cyan-500/50 text-cyan-500/70 font-bold cursor-help rounded px-0.5">{rawWord}</span>;
                }
                if (isRisk) {
                    return <span key={wordId} onClick={(e) => { e.stopPropagation(); handleTokenClick('risk', cleanWord); }} className="text-red-500 font-bold decoration-wavy underline decoration-red-500/50 cursor-help hover:bg-red-900/30 rounded px-0.5">{rawWord}</span>;
                }
                return <span key={wIdx} className="text-slate-500/50 blur-[1px]">{rawWord}</span>;
            }

            // --- VIEW MODE 3: STANDARD RECRUITER VISION ---
            if (isImpact) {
              return (
                <span key={wIdx} onClick={(e) => { e.stopPropagation(); handleTokenClick('impact', cleanWord); }} className="text-green-400 font-bold drop-shadow-[0_0_8px_rgba(74,222,128,0.8)] animate-pulse cursor-help hover:bg-green-900/30 rounded px-0.5 transition-colors">{rawWord}</span>
              );
            }
            if (isVerb) {
              return (
                <span key={wIdx} onClick={(e) => { e.stopPropagation(); handleTokenClick('verb', cleanWord); }} className="text-cyan-400 font-bold drop-shadow-[0_0_8px_rgba(34,211,238,0.8)] cursor-help hover:bg-cyan-900/30 rounded px-0.5 transition-colors">{rawWord}</span>
              );
            }
            if (isRisk) {
              return (
                <span key={wIdx} onClick={(e) => { e.stopPropagation(); handleTokenClick('risk', cleanWord); }} className="text-red-500 font-bold decoration-wavy underline decoration-red-500/50 cursor-help hover:bg-red-900/30 rounded px-0.5 transition-colors">{rawWord}</span>
              );
            }

            return (
              <span key={wIdx} className={`transition-all duration-500 ${currentBlurState ? 'blur-[3px] opacity-30 text-gray-400 select-none' : 'text-slate-300'}`}>
                {rawWord}
              </span>
            );
          })}
        </div>
      );
    });
  };

  const getHeaderUI = () => {
      switch (viewMode) {
          case 'audit':
              return (
                  <div className="flex items-center gap-3">
                      <div className="p-2 bg-yellow-500 rounded-lg shadow-[0_0_15px_rgba(234,179,8,0.5)]"><MousePointer2 className="text-slate-900" size={24} /></div>
                      <div>
                          <h2 className="text-xl font-black tracking-widest uppercase text-white">Self-Audit Engaged</h2>
                          <p className="text-xs text-yellow-300 font-mono">Tap the strongest verbs and metrics before time runs out.</p>
                      </div>
                  </div>
              );
          case 'comparison':
              return (
                  <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-500 rounded-lg shadow-[0_0_15px_rgba(16,185,129,0.5)]"><Layers className="text-white" size={24} /></div>
                      <div>
                          <h2 className="text-xl font-black tracking-widest uppercase text-white">Intel Reveal</h2>
                          <p className="text-xs text-emerald-300 font-mono">Compare your highlights against Recruiter Vision.</p>
                      </div>
                  </div>
              );
          default:
              return (
                  <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-600 rounded-lg shadow-[0_0_15px_rgba(37,99,235,0.5)]"><Layers className="text-white" size={24} /></div>
                      <div>
                          <h2 className="text-xl font-black tracking-widest uppercase text-white">Recruiter Vision</h2>
                          <p className="text-xs text-blue-300 font-mono">Simulating 6-Second Skim Protocol...</p>
                      </div>
                  </div>
              );
      }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-md flex flex-col items-center justify-center p-4 md:p-8"
      onClick={() => setActiveTip(null)} 
    >
      {/* HEADER HUD */}
      <div className="w-full max-w-4xl flex justify-between items-center mb-6 text-white border-b border-white/10 pb-4">
        {getHeaderUI()}
        
        <div className="flex gap-4 items-center">
           {viewMode === 'audit' && (
               <div className="text-3xl font-black font-mono bg-white/10 px-4 py-2 rounded-lg text-yellow-400 drop-shadow-md">
                   00:{Math.ceil(timeLeft).toString().padStart(2, '0')}
               </div>
           )}

           {viewMode !== 'audit' && (
               <div className="hidden md:flex items-center gap-4 text-xs font-bold uppercase tracking-wider">
                  <div className="flex items-center gap-1 text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.5)]">
                     <TrendingUp size={14} /> Impact (Metrics)
                  </div>
                  <div className="flex items-center gap-1 text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.5)]">
                     <Zap size={14} /> Leadership (Verbs)
                  </div>
                  <div className="flex items-center gap-1 text-red-500">
                     <AlertTriangle size={14} /> Risk (Vague)
                  </div>
               </div>
           )}
           
           {viewMode === 'standard' && (
               <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white">
                 <X size={24} />
               </button>
           )}
        </div>
      </div>

      {/* DOCUMENT VIEWER */}
      <div className="w-full max-w-4xl flex-1 overflow-hidden relative bg-slate-950 rounded-xl border border-slate-800 shadow-2xl">
         
         {/* THE SPATIAL MASK (F-Pattern Simulator) */}
         {viewMode === 'audit' && (
             <div 
                 className="absolute inset-0 z-40 pointer-events-none transition-all duration-500" 
                 style={maskStyle}
             />
         )}

         {/* AGGRESSIVE BLUR OVERLAY */}
         <AnimatePresence>
            {isAggressivelyBlurred && (
               <motion.div 
                 initial={{ opacity: 0, scale: 0.95 }}
                 animate={{ opacity: 1, scale: 1 }}
                 exit={{ opacity: 0, scale: 0.95 }}
                 className="absolute inset-0 z-50 flex items-center justify-center bg-slate-950/50 backdrop-blur-sm pointer-events-none p-6"
               >
                 <div className="bg-slate-900 border border-blue-500/50 p-8 rounded-2xl shadow-2xl max-w-lg text-center">
                    <p className="text-xl md:text-2xl font-black text-white leading-snug">
                      That was all the time a recruiter spends on average on a resume. Did yours stand out?
                    </p>
                 </div>
               </motion.div>
            )}
         </AnimatePresence>

         <div className="absolute inset-0 pointer-events-none z-20 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[length:100%_2px,3px_100%] bg-repeat" />
         
         {viewMode === 'standard' && (
            <button 
                onClick={(e) => { e.stopPropagation(); setIsBlurred(!isBlurred); }}
                className="absolute bottom-6 right-6 z-30 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-2 transition-transform hover:scale-105"
            >
                {isBlurred ? <Eye size={18} /> : <EyeOff size={18} />}
                {isBlurred ? "Reveal Details" : "Enable Blur"}
            </button>
         )}

         {viewMode === 'comparison' && !reportData && (
             <div className="absolute inset-0 z-30 bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center">
                 <Loader2 size={48} className="animate-spin text-blue-500 mb-4" />
                 <p className="text-white font-bold tracking-widest uppercase text-sm">Syncing AI Analysis...</p>
             </div>
         )}

         <div className="w-full h-full overflow-y-auto p-8 md:p-12 custom-scrollbar text-left relative z-10">
            {processText(resumeText)}
         </div>
      </div>

      {/* FOOTER ACTIONS & TIPS */}
      <div className="w-full max-w-4xl mt-6 min-h-[96px]">
        {viewMode === 'comparison' && reportData ? (
             <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-slate-800 border border-slate-600 p-4 rounded-xl shadow-2xl">
                 <div className="flex-1">
                     <p className="text-slate-300 text-sm font-medium">
                         <span className="text-green-400 font-bold">Glowing Green</span> = Perfect Match. <span className="text-yellow-500 line-through">Yellow</span> = False Positive. <span className="text-cyan-400 border border-dashed px-1">Dashed</span> = Missed Target.
                     </p>
                 </div>
                 <div className="flex gap-3 w-full sm:w-auto">
                     <button onClick={onRecalibrate} className="flex-1 sm:flex-none px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2">
                         <RefreshCcw size={18} /> Recalibrate Draft
                     </button>
                     <button onClick={onProceed} className="flex-1 sm:flex-none px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg shadow-lg hover:shadow-emerald-500/50 transition-all flex items-center justify-center gap-2">
                         <CheckCircle size={18} /> Call Grade API
                     </button>
                 </div>
             </div>
        ) : (
            <AnimatePresence mode="wait">
                {activeTip ? (
                    <motion.div 
                        key="tip"
                        initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }}
                        className="w-full h-full bg-slate-800 border border-slate-600 p-4 rounded-xl shadow-2xl flex items-start gap-3"
                    >
                        <Info className={`w-5 h-5 mt-0.5 ${activeTip.color}`} />
                        <div>
                            <h4 className={`font-black uppercase text-xs tracking-widest mb-1 ${activeTip.color}`}>{activeTip.title}</h4>
                            <p className="text-sm text-slate-200 leading-snug">{activeTip.msg}</p>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div 
                        key="default"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="w-full h-full flex flex-col items-center justify-center text-center"
                    >
                        <p className="text-white/60 text-sm font-medium">
                        <span className="text-green-400 font-bold">Goal:</span> Your resume must glow with <span className="text-green-400">Numbers</span> and <span className="text-cyan-400">Actions</span> to pass the blur test.
                        </p>
                        {viewMode === 'standard' && (
                            <p className="text-xs text-blue-300/70 mt-1 flex items-center gap-1"><Info size={12} /> (Click any colored word to learn why it's highlighted)</p>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        )}
      </div>

    </motion.div>
  );
}